import React, { useEffect, useMemo, useState } from "react";
import { Button, Button as ButtonAntd, Popconfirm, Spin, Tooltip, message } from "antd";
import { CheckCircleTwoTone, ExclamationCircleTwoTone, InfoCircleOutlined, LoadingOutlined, MinusCircleTwoTone, ReloadOutlined } from "@ant-design/icons";
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { DownloadsTableContainer } from "./downloads-table.styles";
import { DownloadItem } from "../../../types/interfaces";
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable, type MRT_RowSelectionState } from "material-react-table";
import Modal from "antd/es/modal/Modal";
import { batchDownload, deleteDownload, getDownloadList, singleDownload } from "./downloadLambdaUtils";
import CustomLoader from "../../common/CustomLoader";
import { downloadFileFromURL } from "./downloadUtils";
import { DownloadItemStatus } from "../../../common/constant";
import { useNavigate } from "react-router-dom";

export const DownloadsTable = () => {
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [tableData, setTableData] = useState<DownloadItem[]>([]);
    const [selectedRowIdsArray, setSelectedRowIdsArray] = useState<string[]>([]);
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
    const [limitDownload, setLimitDownload] = useState<boolean>(true);
    const [isBatchDownloading, setIsBatchDownloading] = useState<boolean>(false);
    const [show, setShow] = useState<boolean>(true);
    const navigate = useNavigate();

    const downloadData = [
        { key: "download_name", size: 90 },
        { key: "download_size", size: 75 },
        { key: "actions", size: 70 },
        { key: "status", size: 50}
        // { key: "operation", size: 250 },
    ];

    const columns = useMemo<MRT_ColumnDef<DownloadItem>[]>(() => {
        if (tableData.length === 0) {
            return [];
        }
        return downloadData.map(({ key, size }) => {
            if (key === "actions") {
                return {
                    accessorKey: key,
                    enableSorting: false,
                    header: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                    size: size,
                    Cell: ({ row }) => {
                        let status = row.original.status.toString().toLowerCase();
                        const limit = row.original.limit
                        const isProcessing = row.original.isProcessing
                        const isDeleteProcessing = row.original.isDeleteProcessing
                        return (
                            <div style={{ display: "flex", gap: "10px" }}>
                                {
                                    isProcessing ? <LoadingOutlined /> : (
                                        <>
                                            {limit ?
                                                (
                                                    <Popconfirm
                                                        placement="topRight"
                                                        title="You have already downloaded this, would you like to download again?"
                                                        onConfirm={() => onDownload(row.original)}
                                                        okText="Yes"
                                                        cancelText="No"
                                                    >
                                                        <ButtonAntd disabled={status === "error"} shape="circle" icon={<CloudDownloadOutlinedIcon />} />
                                                    </Popconfirm>
                                                ) : (
                                                    <ButtonAntd disabled={status === "error"} shape="circle" icon={<CloudDownloadOutlinedIcon onClick={() => onDownload(row.original)} />} />
                                                )
                                            }
                                        </>
                                    )
                                }
                                {
                                    isDeleteProcessing ? <LoadingOutlined /> : (
                                        <>
                                            <Popconfirm
                                                placement="topRight"
                                                title="Are you sure to delete this item?"
                                                onConfirm={() => onDelete(row.original)}
                                                okText="Yes"
                                                cancelText="No"
                                                disabled={isProcessing}
                                            >
                                                <ButtonAntd shape="circle" icon={<MinusCircleTwoTone twoToneColor="#ff0000" />} />
                                            </Popconfirm>
                                        </>
                                    )
                                }
                                
                            </div>
                        )
                    }
                };
            }

            if (key === "download_name") {
                return {
                    accessorKey: key,
                    enableSorting: true,
                    header: "Download Name",
                    size: size,
                    Cell: ({ row }) => {
                        const { download_id, download_name } = row.original;
                        return (
                            <div style={{ display: "flex", alignItems: "center" , justifyContent: "space-between" }}>
                                <span>{download_name}</span>
                                <Tooltip title={`Download ID: ${download_id}`} >
                                    <InfoCircleOutlined style={{ width: 14, height: 14, marginLeft: 5, cursor: "pointer" }} />
                                </Tooltip>
                            </div>
                        );
                    }
                };
            }

            if(key === "status") {
                return {
                    accessorKey: key,
                    enableSorting: false,
                    header: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                    size: size,
                    Cell: ({ row }) => {
                        let status = row.original.status.toString().toLowerCase();
                        return  (
                            <div style={{ color: "black" }} >
                                {
                                    status === "processing" &&
                                    <LoadingOutlined />
                                }
                                {
                                    status === "done" &&
                                    <CheckCircleTwoTone twoToneColor={"#52c41a"} />
                                }
                                {
                                    status === "error" &&
                                    <ExclamationCircleTwoTone twoToneColor={"#ff0000"} />
                                    // <ButtonAntd shape="circle" icon={<ExclamationCircleTwoTone twoToneColor={"#ff0000"} />} />
                                }
                            </div>
                        )
                    }
                };
            }
            
            let data = {
                accessorKey: key,
                header: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                size: size,
            };

            switch (key) {
                case "download_size":
                    console.log(key)
                    let columnName = key.split("_")[1];
                    data.header = columnName.charAt(0).toUpperCase() + columnName.slice(1);
                    break;
                default:
                    break;
            }
            return data
        })
    }, [tableData]);

    const table = useMaterialReactTable<DownloadItem>({
        columns,
        data: tableData,
        enableSorting: true,
        enableRowSelection: true, // Disable sorting
        positionToolbarAlertBanner: 'none', // No alert banner in toolbar
        initialState: {
            showGlobalFilter: true,
        },
        getRowId: (row) => row.download_id, //give each row id
        onRowSelectionChange: setRowSelection, //connect internal row selection state to your own
        state: { rowSelection },
    });

    useEffect(() => {
        loadDownloadsList();
    }, []);

    useEffect(() => {
        let isLimit = false
        selectedRowIdsArray.forEach((id) => {
            const record = tableData.find((item) => item.download_id === id);
            if(record && record.limit) {
                isLimit = true
            }
        });
        setLimitDownload(isLimit);
        
    }, [selectedRowIdsArray]);


    const loadDownloadsList = () => {
        setTableData([]);
        setDataLoading(false);
        setRowSelection({});
        setDataLoading(true);
        getDownloadList()
            .then((res) => {
                const data = JSON.parse(res);
                setTableData(data);
                setDataLoading(false);
            })
            .catch((err) => {
                message.error("Error getting downloads.");
                console.error(err);
                setTableData([]);
                setDataLoading(false);
            });
    };

    const onClose = () => {
        setShow(false);
        navigate('/')
    };

    const onDownload = (record: DownloadItem) => {
        if(record.status === DownloadItemStatus.Processing) {
            message.warning("Data is still processing, Please try after some time", 2);
            return;
        }
        if(record.status === DownloadItemStatus.Error) {
            message.warning("Error", 2);
            return;
        }
        record.isProcessing = true;
        setTableData([...tableData]);
        singleDownload(record)
            .then((res) => {
                downloadFileFromURL(res, record.download_name + record.extension)
                const newTableData = tableData.map((item) => {
                    if (item.download_id === record.download_id) {
                        item.limit = true;
                    }
                    return item;
                })
                setTableData(newTableData);
            })
            .catch((err) => {
                message.error(err, 2);
                console.error(err);
            }).finally(() => {
                record.isProcessing = false;
                setTableData([...tableData]);
            });
    };

    const onDelete = (record: DownloadItem) => {
        const newData : DownloadItem[] = []
        tableData.forEach((item) => {
            if(item.download_id === record.download_id) {
                item.isDeleteProcessing = true
            }
            newData.push(item)
        })
        setTableData(newData);
        deleteDownload(record)
          .then((res) => {
            const data = tableData.filter(
              (item) => item.download_id !== record.download_id
            );
            setTableData(data);
            message.success(res, 2);
          })
          .catch((err) => {
            message.error(err, 2);
            console.error(err);
            const newData: DownloadItem[] = [];
            tableData.forEach((item) => {
              if (item.download_id === record.download_id) {
                item.isDeleteProcessing = false;
              }
              newData.push(item);
            });
            setTableData(newData);
          });
        
    };

    const handleBatchDownload = (recordIds: string[]) => {
        setIsBatchDownloading(true);
        batchDownload(recordIds)
        .then((res) => {
            const result = JSON.parse(res);
            const downloads = Object.keys(result);

            for (let index = 0; index < downloads.length; index++) {
                const id = downloads[index];
                const record = tableData.find((item) => item.download_id === id);
                const data = result[id];
                const downloadName = record ? record.download_name : "download";
                const extension = record ? record.extension : ".las";
                // NOTE: FILENAME IS SET FROM LAMBDA FUNCTION. SETTING IT HERE HAS NO EFFECT.

                if(data.status === "Success") {
                    downloadFileFromURL(data.url, downloadName + extension)
                    if (record) {
                        record.limit = true;
                    }
                }else{
                    console.log("Error - " + data.message)
                }
            }
            setTableData([...tableData]);
            setSelectedRowIdsArray([...selectedRowIdsArray])
            return // Lambda function is not implemented yet
        })
        .catch((err) => {
            message.error(err, 2);
            console.error(err);
        }).finally(() => {
            setIsBatchDownloading(false);
        });
    };

    useEffect(() => {
        const selectedRowIds = Object.keys(rowSelection);
        setSelectedRowIdsArray(selectedRowIds);
      }, [rowSelection]);

    return (
        <>
            <Modal
                title={
                    <>
                        <span style={{ color: "#fff", fontSize: "20px" }}>
                            Downloads
                        </span>
                        <ReloadOutlined onClick={loadDownloadsList} style={{position: "absolute", right : "55px", top : "18px"}} />
                    </>
                }
                centered
                open={show}
                footer={
                    !tableData.length ? null :
                        [
                            <Button key="apply" onClick={() => handleBatchDownload(selectedRowIdsArray)}
                                loading={isBatchDownloading}
                                disabled={selectedRowIdsArray.length === 0 || limitDownload} className="apply-button download-apply-button">
                                Batch Download
                            </Button>
                        ]
                    }
                onCancel={onClose}
                style={{ width: "500px" }}
                className="process_modal_search download_modal">
                    <DownloadsTableContainer>
                        <Spin spinning={dataLoading}>
                            <div className="inputData_table_searchable download_table_block">
                                <MaterialReactTable table={table} />
                            </div>
                        </Spin>
                    </DownloadsTableContainer>
            </Modal>
        </>
    );
};
